/* Phase 4 §9 — engine-derived UI state.
 *
 * The store mirrors engine selection events into Solid signals. It never
 * holds document content — only the geometry the engine reports (§9
 * invariant). Engine rects arrive in canvas device pixels; they are
 * converted to CSS pixels here so the DOM overlays land correctly on
 * high-DPI displays. */
import { createSignal } from 'solid-js';
import type { EngineClient } from '../engine/engine-client';
import type {
    A11yNode,
    A11yTable,
    Alignment,
    AttrsMixed,
    BlockPath,
    BridgeStoryRef,
    Direction,
    Event,
    ImageWrapMode,
    TextBoxHop,
    LogicalPos,
    LogicalRange,
    Rect,
    SelectionKind,
    TextAttrs,
} from '../engine/types';
import { topPos } from '../engine/types';

/* ——— Shared page-geometry constants ———
 * ONE source of truth for the pointer path, the per-page overlays, and
 * the layout-config dispatches, so their math can never disagree. Page
 * tops derived from these are uniform-A4 approximations — exact
 * per-section geometry (landscape / custom page sizes) needs
 * engine-reported page tops and is tracked separately. */

/** A4 page height in layout pt — engine `PageGeometry::a4()`. */
export const PAGE_H_PT = 841.9;
/** Inter-page gap in layout pt — engine `render::scene::PAGE_GAP_PT`. */
export const PAGE_GAP_PT = 48;
/** Print → screen DPI conversion: 1 pt (engine) = 1/72 inch; 1 CSS px =
 *  1/96 inch; so `screen_dpi_scale = 96 / 72 = 4 / 3`. Multiplied into
 *  the engine's `device_pixel_ratio` so an A4 page renders at the same
 *  physical size as Word / Google Docs at 100% zoom. */
export const SCREEN_DPI_SCALE = 4 / 3;
/** Exact page height in CSS px (841.9 × 4/3 = 1122.533…). NOT the rounded
 *  1123 — a 0.467 px/page error compounds to over half a line by page 30. */
export const PAGE_H_CSS = PAGE_H_PT * SCREEN_DPI_SCALE;
/** Inter-page gap in CSS px (48 × 4/3 = 64) at 100 % zoom — the
 *  `.editor-pages` gap is this × the engine zoom (issue #280). */
export const PAGE_GAP_CSS = PAGE_GAP_PT * SCREEN_DPI_SCALE;
/** A4 page width in layout pt — engine `PageGeometry::a4()`. */
export const PAGE_W_PT = 595.3;
/** A4 page width in CSS px at 100 % zoom (595.3 × 4/3 = 793.73…). */
export const PAGE_W_CSS = PAGE_W_PT * SCREEN_DPI_SCALE;

/* ——— Issue #280 — zoom + the device-px ↔ CSS-px ratio ———
 *
 * Zoom visibly resizes the page: every page card's CSS box is the
 * engine's device-px page size divided by `deviceRatio()`, so 150 %
 * paints a 1.5× larger card instead of a denser bitmap in a fixed box.
 *
 * The engine paints at `scale = base × zoom` with `base = dpr × 4/3`,
 * capped at `MAX_PAINT_SCALE` device px per pt (never below `base`) so a
 * zoomed-in page's backing store stays inside the memory budget (#63 —
 * every mounted page holds one). Below the cap the ratio IS
 * `devicePixelRatio` (crisp); past it the capped bitmap is stretched to
 * the full zoomed size — the page still grows exactly with the zoom.
 * The engine owns the zoom (`SELECTION_CHANGED.zoom`, `ZOOM_PENDING`,
 * `RECOVERED`); the reply to a `SET_ZOOM` lands before the repaint's
 * `PAINTED` broadcast, so geometry converted at receipt always uses the
 * ratio it was painted at. */

/** MIRROR of the engine's `MAX_PAINT_SCALE` (`crates/engine-wasm`). */
export const MAX_PAINT_SCALE = 4;

const [engineZoomSig, setEngineZoomSig] = createSignal(1);

/** The ENGINE's current user zoom fraction (reactive). */
export function engineZoom(): number {
    return engineZoomSig();
}

/** Device px (engine geometry) per CSS px at `zoom` — `devicePixelRatio`
 *  until the paint-scale cap bites (see above). */
export function deviceRatioFor(zoom: number): number {
    const dpr = window.devicePixelRatio || 1;
    const base = dpr * SCREEN_DPI_SCALE;
    const scale = Math.min(base * zoom, Math.max(MAX_PAINT_SCALE, base));
    return scale / (SCREEN_DPI_SCALE * zoom);
}

/** Device px per CSS px at the engine's current zoom (reactive). */
export function deviceRatio(): number {
    return deviceRatioFor(engineZoomSig());
}

/** The engine's effective paint scale (device px per layout pt). */
export function paintScale(): number {
    return deviceRatio() * SCREEN_DPI_SCALE * engineZoomSig();
}

/* Issue #26 — engine-reported per-page geometry (device px, from the
 * `Painted` event). The signal drives the reactive overlays; the
 * module-level mirror lets the non-reactive pointer path read the
 * latest tops at event time without threading the store through
 * `attachPointer`. */
export interface PageGeometry {
    tops: number[];
    heights: number[];
    /** Issue #280 — per-page widths (device px); empty from pre-#280
     *  producers (A4 fallback). */
    widths: number[];
    /** Issue #280 — the device-px-per-CSS-px ratio these numbers were
     *  painted at (captured at receipt, so a card never mixes a new
     *  zoom's ratio with the previous paint's geometry). */
    ratio: number;
}
let latestPageTops: number[] | null = null;

/** Device-px top of page `idx` from the last paginated paint, or `null`
 *  before one lands (callers fall back to the uniform constants). */
export function enginePageTopDevice(idx: number): number | null {
    return latestPageTops?.[idx] ?? null;
}

/* Phase 3 (#39) — per-page margins + heights (device px) mirrored for
 * the non-reactive pointer path's header/footer zone gate, and the
 * active story ref for its already-in-this-story exemption. Margins
 * ride every `Painted` (real + synthetic), so the gate stays exact for
 * mixed-geometry sections without a mid-gesture worker round-trip. */
let latestPageMargins: { tops: number[]; bottoms: number[] } | null = null;
let latestPageHeights: number[] | null = null;
/* Issue #71 — per-page EFFECTIVE body extents (page-local device px).
 * Once a band spills past its margin the paginator pushes the body
 * edge, and the whole painted band must stay double-clickable — these
 * supersede the raw margins for the zone gate when present. */
let latestContentExtents: { tops: number[]; bottoms: number[] } | null = null;

const [editingStorySig, setEditingStorySig] = createSignal<
    BridgeStoryRef | undefined
>(undefined);

/** The active header/footer story (reactive) — also read at event time
 *  by the pointer path. */
export function editingStoryForPointer(): BridgeStoryRef | undefined {
    return editingStorySig();
}

/* Issue #77 — the Alt+F9 field-code view flag, mirrored from every
 * `SelectionChanged` so the key handler can toggle it without a
 * round-trip (the engine owns the state; this is the last broadcast). */
const [fieldCodeViewSig, setFieldCodeViewSig] = createSignal(false);

/** Issue #77 — `true` while field codes are shown (read at key time). */
export function fieldCodeViewForKeys(): boolean {
    return fieldCodeViewSig();
}

/**
 * Which margin band a PAGE-LOCAL device-px Y lands in on page `idx`,
 * or `null` for the content area / before the first paginated paint
 * (no gate — pre-paint clicks keep the legacy caret behaviour, the
 * same fallback-then-exact duality the selection overlays use).
 */
export function headerFooterZoneAt(
    pageIdx: number,
    localDeviceY: number,
): 'Header' | 'Footer' | null {
    /* Issue #71 — prefer the effective content extents (band-intrusion
     * aware); fall back to raw margins for pre-extents paints. */
    const top =
        latestContentExtents?.tops[pageIdx] ?? latestPageMargins?.tops[pageIdx];
    const contentBottomY = latestContentExtents?.bottoms[pageIdx];
    const bottom = latestPageMargins?.bottoms[pageIdx];
    const height = latestPageHeights?.[pageIdx];
    if (top === undefined || height === undefined) {
        return null;
    }
    const footerStartY =
        contentBottomY ?? (bottom !== undefined ? height - bottom : undefined);
    if (footerStartY === undefined) return null;
    if (localDeviceY >= 0 && localDeviceY < top) return 'Header';
    if (localDeviceY > footerStartY && localDeviceY <= height) return 'Footer';
    return null;
}

/* Issue #36 — same module-level-mirror pattern for the selection view:
 * the pointer path decides on right-click whether the press lands inside
 * the active selection (preserve it for the context menu) or outside
 * (move the caret, Word semantics) without threading the store through
 * `attachPointer`. Rects are CSS px, document-absolute. */
let latestSelectionView: { rects: Rect[]; kind: SelectionKind } = {
    rects: [],
    kind: { kind: 'LINEAR' },
};

/** Latest selection rects + kind from `SELECTION_CHANGED`, for the
 *  non-reactive pointer path. */
export function selectionViewForPointer(): { rects: Rect[]; kind: SelectionKind } {
    return latestSelectionView;
}

/* Issue #44 — inline-image geometry + selection. `imageRectsSig` holds
 * every image's CSS-px rect + resize address (refreshed after each paint
 * via GET_IMAGE_RECTS); `selectedImageSig` is the image the user clicked,
 * driving the resize-handle overlay. Both are module-level Solid signals
 * (one editor instance) so the non-reactive pointer path can select an
 * image on pointerdown and the reactive overlay can render its handles. */
export interface ImageRectCss {
    path: BlockPath;
    at: number;
    /** Issue #224 — the `DocumentTree::media` key (`ImageRect.media_key`),
     *  not the deprecated `rel_id`. */
    mediaKey: string;
    rect: Rect; // CSS px, document-absolute
    widthEmu: number;
    heightEmu: number;
    /** Issue #69 — `true` for a floating (`<wp:anchor>`) image: the body
     *  drags to reposition it (`MOVE_IMAGE`); inline images only resize. */
    floating: boolean;
    /** Issue #69 — top-left of the float's reference frame, CSS px,
     *  document-absolute (same space as `rect`; 0,0 for inline images).
     *  A dragged rect converts back to frame-relative EMU offsets with the
     *  pure ratio `widthEmu / rect.w`, like the resize handles. */
    frameX: number;
    frameY: number;
    /** Issue #82 — the floating image's text-wrap mode (`undefined` for
     *  an inline image); drives the image toolbar's wrap picker. */
    wrap: ImageWrapMode | undefined;
    /** Issue #206 — the text-box story chain the picture lives in (empty
     *  for a body picture); `path` is rooted in that story. Handed back as
     *  the `story` of RESIZE_IMAGE / MOVE_IMAGE / SET_IMAGE_WRAP. */
    story: TextBoxHop[];
    /** Issue #206 — the owning story's `outer/inner` id (`''` for body). */
    storyRid: string;
    /** Issue #554 — the picture's paragraph runs right-to-left, so its
     *  visual left edge is the byte offset AFTER the sentinel. */
    rtl: boolean;
}
export interface ImageAddr {
    path: BlockPath;
    at: number;
    /** Issue #206 — the picture's text-box story chain (empty = body). */
    story: TextBoxHop[];
}
const [imageRectsSig, setImageRectsSig] = createSignal<ImageRectCss[]>([]);
const [selectedImageSig, setSelectedImageSig] = createSignal<ImageAddr | null>(null);

/** Latest image rects (CSS px) for the non-reactive pointer path. */
export function imageRectsForPointer(): ImageRectCss[] {
    return imageRectsSig();
}

const samePath = (a: BlockPath, b: BlockPath): boolean =>
    a.steps.length === b.steps.length &&
    a.steps.every((s, i) => {
        const o = b.steps[i]!;
        if (s.kind !== o.kind) return false;
        if (s.kind === 'BLOCK' && o.kind === 'BLOCK') return s.idx === o.idx;
        if (s.kind === 'CELL' && o.kind === 'CELL')
            return s.row === o.row && s.col === o.col;
        return false;
    });

/** `true` when two image addresses name the same picture — same story
 *  chain (issue #206: a box picture's path is story-rooted, so `(path,
 *  at)` alone can collide with a body picture), path and anchor byte. */
export const sameImageAddr = (a: ImageAddr, b: ImageAddr): boolean =>
    a.at === b.at &&
    samePath(a.path, b.path) &&
    a.story.length === b.story.length &&
    a.story.every((h, i) => {
        const o = b.story[i]!;
        return h.at === o.at && samePath(h.path, o.path);
    });

/** Select the image under a document-absolute CSS point, if any. Returns
 *  true when an image was hit (and selected); false clears the selection.
 *  Called synchronously by the pointer path before it places a caret.
 *  Issue #206 — `textBoxOnly` limits the hit to pictures inside text-box
 *  stories (the pointer path while a text box is being edited). Nested
 *  pictures are listed after their box's, so the innermost hit wins. */
export function selectImageByPoint(
    cssX: number,
    cssY: number,
    textBoxOnly = false,
): boolean {
    const hits = imageRectsSig().filter(
        (im) =>
            (!textBoxOnly || im.story.length > 0) &&
            cssX >= im.rect.x &&
            cssX <= im.rect.x + im.rect.w &&
            cssY >= im.rect.y &&
            cssY <= im.rect.y + im.rect.h,
    );
    const hit = hits[hits.length - 1];
    if (hit) {
        setSelectedImageSig({ path: hit.path, at: hit.at, story: hit.story });
        return true;
    }
    setSelectedImageSig(null);
    return false;
}

/** Issue #554 — the selected picture's current rect, for the non-reactive
 *  key path (`undefined` when nothing is selected or the engine no longer
 *  lists it). */
export function selectedImageForKeys(): ImageRectCss | undefined {
    const sel = selectedImageSig();
    if (!sel) return undefined;
    return imageRectsSig().find((r) => sameImageAddr(r, sel));
}

/** Clear any selected-image state (e.g. on a caret-placing click). */
export function clearSelectedImage(): void {
    setSelectedImageSig(null);
}

/** The current selection: its logical range plus rendered rectangles. */
export interface SelectionView {
    range: LogicalRange;
    rects: Rect[];
}

/** Undo/redo availability — drives the toolbar's Undo/Redo buttons. */
export interface UndoState {
    canUndo: boolean;
    canRedo: boolean;
}

const EMPTY_RANGE: LogicalRange = {
    start: topPos(0, 0),
    end: topPos(0, 0),
};

/** Device-pixel rect → CSS-pixel rect. */
function toCssRect(r: Rect, dpr: number): Rect {
    return { x: r.x / dpr, y: r.y / dpr, w: r.w / dpr, h: r.h / dpr };
}

/**
 * Subscribe to the engine and expose its selection state as Solid signals.
 * Created once by `App` and passed to the overlays.
 */
export function createEngineStore(client: EngineClient) {
    const [caret, setCaret] = createSignal<Rect | null>(null);
    const [caretLogical, setCaretLogical] = createSignal<LogicalPos | null>(null);
    const [selection, setSelection] = createSignal<SelectionView>({
        range: EMPTY_RANGE,
        rects: [],
    });
    const [attrsAtCaret, setAttrsAtCaret] = createSignal<TextAttrs | null>(null);
    /* Phase 9b — per-flag "mixed across the selection" bitmap. The
       toolbar reads it to switch each B/I/U/S button between
       OFF/ON/INDETERMINATE so a selection straddling a styled boundary
       doesn't mis-render the start position's value as the whole
       selection's. Default to all-false on no selection. */
    const [attrsMixed, setAttrsMixed] = createSignal<AttrsMixed>({
        bold: false,
        italic: false,
        underline: false,
        strike: false,
    });
    const [undoState, setUndoState] = createSignal<UndoState>({
        canUndo: false,
        canRedo: false,
    });
    /* Effective alignment of the caret's paragraph + the document base
       direction — together they drive the toolbar's alignment picker. */
    const [paragraphAlignment, setParagraphAlignment] = createSignal<Alignment>('Start');
    const [baseDirection, setBaseDirection] = createSignal<Direction>('Ltr');
    /* Phase 9c — effective paragraph base direction (`<w:bidi>`),
       tri-state: `Ltr` / `Rtl` when every paragraph in the range
       agrees, `null` when they disagree → toolbar's LTR/RTL buttons
       render INDETERMINATE. Distinct from `baseDirection`, which is
       the document-level direction for the alignment picker; this
       reflects the SELECTION's paragraph direction. */
    const [paragraphDirection, setParagraphDirection] = createSignal<Direction | null>('Ltr');
    /* Issue #42 — the caret paragraph's `<w:numPr><w:ilvl>`, or `undefined`
       outside any list. Drives the Tab-key handler's demote/promote
       branch in `HiddenInput`. */
    const [listIlvl, setListIlvl] = createSignal<number | undefined>(undefined);
    const [announcement, setAnnouncement] = createSignal('');
    /* Sprint 10 — separate `aria-live="assertive"` channel. Engine
       emits this priority only for errors / blocked actions; the UI
       interrupts the screen reader instead of waiting for a pause. */
    const [assertiveAnnouncement, setAssertiveAnnouncement] = createSignal('');
    /* Issue #48 — UI-side (non-engine) failure channel. Browser-API
       failures (e.g. a clipboard write blocked by lost focus or
       permissions) never produce an `Event::Error` — the engine was
       never involved — so the shell needs its own signal for the
       visible error banner. Setting a message also re-emits it on the
       assertive live region (mirroring the `Event::Error` handling
       below) so screen readers hear it; clearing (`null`) is silent. */
    /* `equals: false` so a REPEATED identical failure still re-arms the
       banner's 6 s dismiss timer (createSignal's default === comparator
       would swallow the second set as a no-op). No assertive live-region
       mirror here: the visible banner itself carries role="alert", so a
       second pipe would announce every error twice to screen readers. */
    const [uiError, setUiError] = createSignal<string | null>(null, {
        equals: false,
    });
    /* Mirror of every table in the document — extracted from the a11y
       delta stream so the Table panel can list/target tables by
       BlockPath::top(block_index). The current paragraph-flat
       LogicalPos can't address a cell, so PR 3b targets tables by
       block index rather than by caret position. */
    const [tables, setTables] = createSignal<A11yTable[]>([]);
    /* PR 4 — selection flavour. Linear by default; `TableCells` when the
       drag stayed inside a single table and covered multiple cells. */
    const [selectionKind, setSelectionKind] = createSignal<SelectionKind>({ kind: 'LINEAR' });
    /* Phase 6b — paginated layout reach. `documentHeight` is the engine's
       total Y span (every page + the inter-page gap) in device px; the
       canvas mount divides by `devicePixelRatio` to set its CSS height
       so the browser scrollbar exposes every page. `pageCount` drives
       the document-info UI strip. */
    const [documentHeight, setDocumentHeight] = createSignal(0);
    /* Issue #51 — virtual scroll range. With the viewport cull actually
       firing, `documentHeight` covers only the laid-out band; the
       engine's per-paint estimate (real band + avg-height fudge per
       unlaid block) is what the scroller must expose, or the user can
       never scroll INTO the unlaid tail to trigger EXPAND_LAYOUT. */
    const [estimatedDocumentHeight, setEstimatedDocumentHeight] = createSignal(0);
    const [pageCount, setPageCount] = createSignal(1);
    /* Audit gap C.H1 — `true` once the lazy paginator has laid out every
       block; gates the shell's scroll-driven EXPAND_LAYOUT dispatches
       (an expand past a fully-laid-out tail would repaint for nothing). */
    const [isFullLayout, setIsFullLayout] = createSignal(false);
    const [pageGeometry, setPageGeometry] = createSignal<PageGeometry | null>(null);
    /* Phase 3 (#39) — reactive per-page margins (device px) for the
       StoryModeOverlay's dim + band-outline geometry. */
    const [marginGeometry, setMarginGeometry] = createSignal<{
        tops: number[];
        bottoms: number[];
    } | null>(null);
    /* Issue #505 — reactive twin of `latestContentExtents` (page-local
       device px of each page's EFFECTIVE body top / bottom edge: the
       margins pushed inward by a band that overflows them). The story
       overlay outlines the band the zone gate tests against, so the
       band you double-click, the band that lights up and the band the
       entry caret sits in are one region. */
    const [contentGeometry, setContentGeometry] = createSignal<{
        tops: number[];
        bottoms: number[];
    } | null>(null);
    let nodes: A11yNode[] = [];
    let announced = false;

    const recomputeTables = (): void => {
        const out: A11yTable[] = [];
        for (const n of nodes) if (n.kind === 'TABLE') out.push(n);
        setTables(out);
    };

    /* Issue #44 — query image geometry and mirror it (device px → CSS
       px) into the module-level signal the overlay + pointer path read.
       If the currently-selected image vanished (deleted / undo), drop the
       selection so stale handles never linger. */
    const refreshImageRects = async (): Promise<void> => {
        try {
            const evt = await client.dispatch({ type: 'GET_IMAGE_RECTS' });
            if (evt.type !== 'IMAGE_RECTS') return;
            const dpr = deviceRatio();
            const rects: ImageRectCss[] = evt.images.map((im) => ({
                path: im.path,
                at: im.at,
                mediaKey: im.media_key,
                rect: toCssRect(im.rect, dpr),
                widthEmu: im.width_emu,
                heightEmu: im.height_emu,
                floating: im.floating,
                frameX: im.frame_x / dpr,
                frameY: im.frame_y / dpr,
                wrap: im.wrap,
                story: im.story ?? [],
                storyRid: im.story_rid ?? '',
                rtl: im.rtl ?? false,
            }));
            setImageRectsSig(rects);
            const sel = selectedImageSig();
            if (sel && !rects.some((r) => sameImageAddr(r, sel))) {
                setSelectedImageSig(null);
            }
        } catch (e) {
            console.error('refreshImageRects failed', e);
        }
    };

    client.subscribe((ev: Event) => {
        if (ev.type === 'SELECTION_CHANGED') {
            /* Issue #280 — the zoom rides this very event; adopt it BEFORE
               converting its rects so they use the ratio they were
               produced at. */
            if (ev.zoom !== undefined) setEngineZoomSig(ev.zoom);
            const dpr = deviceRatio();
            const cssRects = ev.rects.map((r) => toCssRect(r, dpr));
            latestSelectionView = { rects: cssRects, kind: ev.selection_kind };
            setCaret(toCssRect(ev.caret, dpr));
            setCaretLogical(ev.range.end);
            setSelection({
                range: ev.range,
                rects: cssRects,
            });
            setAttrsAtCaret(ev.attrs_at_caret);
            setAttrsMixed(ev.attrs_mixed);
            setParagraphDirection(ev.paragraph_direction ?? null);
            setUndoState({ canUndo: ev.can_undo, canRedo: ev.can_redo });
            setParagraphAlignment(ev.paragraph_alignment);
            setBaseDirection(ev.direction);
            setSelectionKind(ev.selection_kind);
            setListIlvl(ev.list_ilvl);
            setEditingStorySig(ev.editing_story);
            setFieldCodeViewSig(ev.field_code_view);
        } else if (ev.type === 'ZOOM_PENDING') {
            /* Issue #239/#280 — a zoom queued before the first paint: the
               boot paint will already be at this zoom. */
            setEngineZoomSig(ev.zoom);
        } else if (ev.type === 'RECOVERED') {
            /* Issue #97/#280 — the respawned engine's restored zoom. */
            setEngineZoomSig(ev.zoom ?? 1);
        } else if (ev.type === 'PAINTED') {
            /* Phase 6b — paginator reach. The engine emits
               `document_height` (device px) and `page_count` on every
               paint; mirror them into signals so `EditorCanvas` resizes
               itself to fit every page and the scrollbar exposes
               them. Edits that grow / shrink the page count flow
               through here. */
            setDocumentHeight(ev.document_height);
            setEstimatedDocumentHeight(ev.estimated_document_height);
            setPageCount(ev.page_count);
            setIsFullLayout(ev.is_full_layout);
            /* Issue #26 — harness paths bypass pagination and emit empty
               arrays; keep the last real geometry in that case. */
            if (ev.page_tops.length > 0) {
                latestPageTops = ev.page_tops;
                latestPageHeights = ev.page_heights;
                setPageGeometry({
                    tops: ev.page_tops,
                    heights: ev.page_heights,
                    widths: ev.page_widths ?? [],
                    ratio: deviceRatio(),
                });
            }
            /* Phase 3 (#39) — margin zones for the pointer gate + the
               story overlay. */
            if (ev.page_margin_tops.length > 0) {
                latestPageMargins = {
                    tops: ev.page_margin_tops,
                    bottoms: ev.page_margin_bottoms,
                };
                setMarginGeometry({
                    tops: ev.page_margin_tops,
                    bottoms: ev.page_margin_bottoms,
                });
            }
            /* Issue #71 — effective body extents (band-intrusion
               aware): the zone gate's preferred truth. */
            if (ev.page_content_tops.length > 0) {
                latestContentExtents = {
                    tops: ev.page_content_tops,
                    bottoms: ev.page_content_bottoms,
                };
                setContentGeometry(latestContentExtents);
            }
            /* Issue #44 — refresh inline-image geometry after a paint so
               the resize-handle overlay tracks images through edits,
               scroll (lazy layout), and resizes. Pure query — no repaint,
               so no feedback loop. Gated on `image_count` so an image-free
               document (the common case) never pays the extra worker
               round-trip; the `imageRects` check still fires the final
               clearing refresh when the last image was just removed. */
            if (ev.image_count > 0 || imageRectsSig().length > 0) {
                void refreshImageRects();
            }
        } else if (ev.type === 'ACCESSIBILITY_TREE_DELTA') {
            /* Mirror the patch stream into the local `nodes` array so the
               Table panel sees the same view the reconciler renders. */
            for (const patch of ev.patches) {
                if (patch.type === 'REPLACE') {
                    nodes = [...patch.tree.nodes];
                } else if (patch.type === 'UPDATE') {
                    nodes[patch.index] = patch.node;
                } else if (patch.type === 'INSERT') {
                    nodes.splice(patch.index, 0, patch.node);
                } else if (patch.type === 'REMOVE') {
                    nodes.splice(patch.index, 1);
                }
            }
            recomputeTables();
            /* The first delta from a fresh engine is a single REPLACE; use its
               node count for the one-time "document loaded" announcement. */
            if (!announced) {
                announced = true;
                const first = ev.patches[0];
                const n = first && first.type === 'REPLACE' ? first.tree.nodes.length : 0;
                setAnnouncement(`Document loaded — ${n} block${n === 1 ? '' : 's'}.`);
            }
        } else if (ev.type === 'ANNOUNCEMENT') {
            /* Sprint 10 — engine-side announcement (priority + message
               computed by the engine; the UI just routes to the right
               aria-live region). Re-emit on every event so a duplicate
               message still narrates (the screen reader otherwise
               skips identical successive content). */
            if (ev.priority === 'Assertive') {
                setAssertiveAnnouncement('');
                queueMicrotask(() => setAssertiveAnnouncement(ev.message));
            } else {
                setAnnouncement('');
                queueMicrotask(() => setAnnouncement(ev.message));
            }
        } else if (ev.type === 'ERROR') {
            /* Sprint 10 — errors interrupt; the engine doesn't emit an
               explicit Assertive announcement for these (Event::Error
               is the primary reply, not a broadcast), so we synthesise
               one client-side. */
            setAssertiveAnnouncement('');
            queueMicrotask(() => setAssertiveAnnouncement(`Error: ${ev.message}`));
        }
    });

    return {
        caret,
        caretLogical,
        selection,
        attrsAtCaret,
        attrsMixed,
        undoState,
        paragraphAlignment,
        baseDirection,
        paragraphDirection,
        listIlvl,
        announcement,
        assertiveAnnouncement,
        uiError,
        setUiError,
        tables,
        selectionKind,
        documentHeight,
        estimatedDocumentHeight,
        pageCount,
        isFullLayout,
        pageGeometry,
        /* Issue #44 — inline-image rects + selection for the resize
           overlay. `imageRects` are CSS px, document-absolute. */
        imageRects: imageRectsSig,
        selectedImage: selectedImageSig,
        setSelectedImage: setSelectedImageSig,
        refreshImageRects,
        /* Phase 3 (#39) — active story for overlays + control gating. */
        editingStory: editingStorySig,
        /* Issue #77 — Alt+F9 field-code view flag. */
        fieldCodeView: fieldCodeViewSig,
        marginGeometry,
        /** Issue #505 — effective body extents per page (device px,
         *  page-local), `null` before a paginated paint. */
        contentGeometry,
        /** Issue #280 — the engine's user zoom (reactive). */
        zoom: engineZoomSig,
        /** Issue #280 — device px per CSS px at the last paint (the
         *  ratio the page geometry + margins were produced at). */
        paintRatio: (): number => pageGeometry()?.ratio ?? deviceRatio(),
        /** CSS-px top of page `idx` — engine-exact once a paginated paint
         *  reported geometry; uniform-A4 fallback (× zoom) before that. */
        pageTopCss: (idx: number): number => {
            const g = pageGeometry();
            const top = g?.tops[idx];
            return g && top !== undefined
                ? top / g.ratio
                : idx * (PAGE_H_CSS + PAGE_GAP_CSS) * engineZoomSig();
        },
        /** CSS-px height of page `idx` — same fallback rules. */
        pageHeightCss: (idx: number): number => {
            const g = pageGeometry();
            const h = g?.heights[idx];
            return g && h !== undefined ? h / g.ratio : PAGE_H_CSS * engineZoomSig();
        },
        /** Issue #280 — CSS-px width of page `idx` — same fallback rules. */
        pageWidthCss: (idx: number): number => {
            const g = pageGeometry();
            const w = g?.widths[idx];
            return g && w !== undefined ? w / g.ratio : PAGE_W_CSS * engineZoomSig();
        },
        /** Issue #280 — CSS size of page `idx`'s CARD: the engine sizes the
         *  canvas backing store to `ceil(device px)`, so the card is that
         *  bitmap ÷ the ratio — one bitmap pixel per device pixel at
         *  100 % (no resampling blur), overlays in the same page-local
         *  CSS space. */
        pageCardCss: (idx: number): { w: number; h: number } => {
            const g = pageGeometry();
            const w = g?.widths[idx];
            const h = g?.heights[idx];
            const z = engineZoomSig();
            return {
                w: g && w !== undefined ? Math.ceil(w) / g.ratio : PAGE_W_CSS * z,
                h: g && h !== undefined ? Math.ceil(h) / g.ratio : PAGE_H_CSS * z,
            };
        },
        /** Issue #280 — inter-page gap in CSS px at the engine zoom (the
         *  engine's `PAGE_GAP_PT × scale`, ÷ the ratio). */
        pageGapCss: (): number => PAGE_GAP_CSS * engineZoomSig(),
        /** Issue #280 — device px → CSS px at the last paint's ratio. */
        deviceToCss: (v: number): number => v / (pageGeometry()?.ratio ?? deviceRatio()),
    };
}

export type EngineStore = ReturnType<typeof createEngineStore>;
